import { useEffect, useState } from "react";
import { Col, Row } from "@/utils/bootstrap";
import { ApiMsgTypes } from "@/api/AppApi/common-interfaces/IResultStamp";
import { useManageCartItem } from "@/api/AppApi/services/cart";
import {
  ICartItem_Product,
  IGetCart,
} from "@/api/AppApi/services/cart/interfaces/cart-interfaces";
import { debounce, getItemCartPropsToPost_product } from "../../cart-functions";
import CartItemIcons from "./cart-item-icons/CartItemIcons";
import ItemQuantityBtn from "./cart-item-btns/item-quantity-btn/ItemQuantityBtn";
import Price from "@/app/components/lib/Price";
import { currencyAtom } from "@/hooks/currencyAtom";
import { useAtom } from "jotai";
import CartItemImg from "./CartItemImg";
import { useTranslation } from "@/hooks/localization/useTranslations";
import CartItemUnitDropdown from "./cart-item-untits-dropdown";
import useEventTrackers from "@/utils/helpers/pixel-helpers/hooks/useEventTrackers/useEventTrackers";
import { useGetCompanyQuery } from "@/api/AppApi/services/company";
import { EventType } from "@/utils/helpers/pixel-helpers/hooks/useEventTrackers/helpers/interfaces";
import ProductPriceInfoButton from "@/app/components/common-builder/product/product-box/product-box-helpers/product-price-info/ProductPriceInfoButton";
import calculatPricesForSpecialPrices from "@/utils/functions/calculatPriceForSpecialPrices";
import NotFieldErrModal from "@/app/components/common-form-builder/form-builder-helpers/NotFieldErrModal";

interface Props {
  cartItem: ICartItem_Product;
  mobile?: boolean;
  menu?: boolean;
}

const CartItemProduct: React.FC<Props> = ({ cartItem, mobile, menu }) => {
  const t = useTranslation();
  const { eventTrackers } = useEventTrackers();
  const { data: CompanyData } = useGetCompanyQuery();
  const show_all_product_units =
    CompanyData?.result?.theme?.components?.product?.show_all_product_units ||
    false;

  const [notFieldErr, setNotFieldErr] = useState<string[]>([]);
  const [currency] = useAtom(currencyAtom);
  const [quantity, setQuantity] = useState(0);
  const [unitId, setUnitId] = useState<number>(cartItem.others.unit_id);
  const selectedUnit = cartItem.others.units.find((item) => item.id === unitId);
  const small = (mobile || menu) ?? false;
  const specialPrices = cartItem.others.selling_prices;

  const { price, price_without_discount } = calculatPricesForSpecialPrices({
    originalPrice: cartItem.price,
    originalPriceWithoutDiscount: cartItem.basic_price,
    unitId,
  })({
    quantity,
    specialPrices,
  });

  const manageCartApi = useManageCartItem({
    onSuccess: (res: IGetCart) => {
      const { coupon } = res.result ?? {};
      if (res?.message?.type === ApiMsgTypes.success) {
        eventTrackers(EventType.ADD_CART, [{ ...cartItem }], {
          currency: currency?.code,
          value: cartItem?.price,
          coupon: coupon?.code ?? "",
          discount: cartItem?.discount,
        });
      }
    },
    onError: (error) => {
      setQuantity(cartItem?.quantity ?? 0);
      setNotFieldErr([error?.response?.data?.message?.text]);
      // console.log(error);
    },
  });
  const handleQuantityChange = (qty: number) => {
    setQuantity(qty);
    debounce(
      manageCartApi.mutate({
        ...getItemCartPropsToPost_product(cartItem),
        unit_id: unitId,
        quantity: qty,
      }),
      300,
    );
  };
  const handleUnitChange = (unitId: number) => {
    setUnitId(unitId);
    let quantity =
      selectedUnit?.min_quantity_in_cart &&
      selectedUnit?.min_quantity_in_cart > 0 &&
      cartItem.quantity < selectedUnit?.min_quantity_in_cart
        ? selectedUnit?.min_quantity_in_cart
        : cartItem.quantity;
    quantity =
      selectedUnit?.max_quantity_in_cart &&
      selectedUnit?.max_quantity_in_cart > 0 &&
      cartItem.quantity > selectedUnit?.max_quantity_in_cart
        ? selectedUnit?.max_quantity_in_cart
        : cartItem.quantity;
    debounce(
      manageCartApi.mutate({
        ...getItemCartPropsToPost_product(cartItem),
        quantity,
        unit_id: unitId,
      }),
      300,
    );
  };

  const handleNoteChange = (note: string) => {
    manageCartApi.mutate({
      ...getItemCartPropsToPost_product(cartItem),
      note: note,
    });
  };
  useEffect(() => {
    setQuantity(cartItem.quantity);
    setUnitId(cartItem.others.unit_id);
  }, [cartItem.quantity, cartItem.others.unit_id]);
  return (
    <>
      {notFieldErr.length > 0 && (
        <NotFieldErrModal errors={notFieldErr} setErrors={setNotFieldErr} />
      )}
      <Row className="m-0 cart-item">
        <CartItemImg
          image_url={cartItem?.thumbnail_url}
          is_new={cartItem?.others?.is_new}
          discount_amount={cartItem?.others?.discount_amount}
          has_discount={cartItem?.others?.discount_amount > 0}
          discount_type={cartItem?.others?.discount_type}
          discount={cartItem?.others?.discount}
        />
        <Col className="p-0">
          <Row className="m-0">
            <Col className="p-0">
              <h3 className="name m-0">{cartItem?.name}</h3>
              {cartItem?.others.attrs_values.length !== 0 && (
                <p className="desc m-0">
                  {cartItem?.others.attrs_values
                    .map((attr) => attr.name)
                    .join(", ")}
                </p>
              )}
              <div className="price-line">
                <Price
                  priceFirst
                  gap={12}
                  price={{ value: price, className: "price" }}
                  priceWithoutDiscount={{
                    value: price_without_discount,
                    className: "price-without-discount",
                  }}
                  sameLine={true}
                />
                {!!(specialPrices || []).length && (
                  <ProductPriceInfoButton priceInfoData={specialPrices} />
                )}
              </div>
            </Col>
            <Col className="p-0 col-auto">
              <CartItemIcons
                small={small}
                cartItem={cartItem}
                setNote={handleNoteChange}
              />
            </Col>
          </Row>
          <Row className="m-0 align-items-end">
            <Col className="p-0">
              {cartItem?.addons?.length > 0 && (
                <div className="item-add-ons">
                  {!menu ? (
                    cartItem?.addons?.map((addon, key) => (
                      <div
                        key={key}
                      >{`${addon.name} (x${addon.quantity})`}</div>
                    ))
                  ) : (
                    <>{`${t.addons}: ${cartItem?.addons.length}`}</>
                  )}
                </div>
              )}
              {cartItem?.free_quantity > 0 && (
                <Row className="m-0 note">{`${t.free_quantity}: ${cartItem?.free_quantity}`}</Row>
              )}
              <Row className="m-0 note">{`${t.notes}: ${cartItem?.note}`}</Row>
            </Col>
            <BottomLayout condition={!!cartItem.others.units?.length}>
              <Col className="p-0 col-auto">
                <ItemQuantityBtn
                  cartItemId={cartItem?.id}
                  quantity={quantity}
                  setQuantity={handleQuantityChange}
                  plusDisabled={
                    quantity >=
                    Math.min(
                      cartItem.others?.quantity_on_hand,
                      selectedUnit?.max_quantity_in_cart &&
                        selectedUnit?.max_quantity_in_cart > 0
                        ? selectedUnit?.max_quantity_in_cart
                        : cartItem?.others.max_quantity_in_cart ?? 999,
                    )
                  }
                  min_quantity_in_cart={
                    selectedUnit?.min_quantity_in_cart &&
                    selectedUnit?.min_quantity_in_cart > 0
                      ? selectedUnit?.min_quantity_in_cart
                      : cartItem.others.min_quantity_in_cart
                  }
                />
              </Col>
              {show_all_product_units && cartItem.others.units?.length ? (
                <Col className="col-8">
                  <CartItemUnitDropdown
                    onSelect={handleUnitChange}
                    selectedId={unitId as number}
                    units={cartItem.others.units}
                  />
                </Col>
              ) : (
                <></>
              )}
            </BottomLayout>
          </Row>
        </Col>
      </Row>
    </>
  );
};

export default CartItemProduct;

const BottomLayout: React.FC<{
  children: React.ReactElement[];
  condition: boolean;
}> = ({ children, condition }) => {
  if (condition) {
    return <Row className="flex-nowrap">{children}</Row>;
  }
  return children;
};
